<template>
  <v-container id="process_instances_root" fluid>
    <!-- HEADER -->
    <v-row class="mb-2 px-4" align="center">
      <v-col cols="12">
        <v-img
          src="/img/id_validation_logo.png"
          max-width="200"
          contain
        />
      </v-col>
      <v-col cols="12">
        <p class="body-1 grey--text text--darken-2">
          {{ $t('trustx.processInstances.description') }}
        </p>
      </v-col>
    </v-row>

    <!-- INLINE FILTERS -->
    <v-card class="mb-4 pa-4">
      <v-row dense align="center">
        <!-- Status -->
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.status"
            :items="statusItems"
            :label="$t('trustx.processInstances.filters.status')"
            outlined
            dense
            hide-details
          />
        </v-col>

        <!-- From Date + Time -->
        <v-col cols="12" md="3">
          <v-menu
            v-model="fromMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                :value="formatDateTime(filters.fromDate, filters.fromTime)"
                :label="$t('trustx.processInstances.filters.from')"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                hide-details
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-card>
              <v-date-picker
                v-if="fromStep === 'date'"
                v-model="filters.fromDate"
                no-title
                @input="fromStep = 'time'"
              />
              <v-time-picker
                v-else
                v-model="filters.fromTime"
                format="24hr"
                no-title
              >
                <v-spacer />
                <v-btn text color="primary" @click="fromMenu = false; fromStep = 'date'">OK</v-btn>
              </v-time-picker>
            </v-card>
          </v-menu>
        </v-col>

        <!-- To Date + Time -->
        <v-col cols="12" md="3">
          <v-menu
            v-model="toMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                :value="formatDateTime(filters.toDate, filters.toTime)"
                :label="$t('trustx.processInstances.filters.to')"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                hide-details
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-card>
              <v-date-picker
                v-if="toStep === 'date'"
                v-model="filters.toDate"
                no-title
                @input="toStep = 'time'"
              />
              <v-time-picker
                v-else
                v-model="filters.toTime"
                format="24hr"
                no-title
              >
                <v-spacer />
                <v-btn text color="primary" @click="toMenu = false; toStep = 'date'">OK</v-btn>
              </v-time-picker>
            </v-card>
          </v-menu>
        </v-col>

        <!-- Buttons -->
        <v-col cols="12" md="3" class="d-flex justify-end">
          <v-btn outlined color="primary" @click="clearFilters">
            <v-icon left small>mdi-filter-remove</v-icon>
            {{ $t('trustx.processInstances.filters.clear') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- DATA TABLE -->
    <v-card>
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="pagination"
        :server-items-length="totalItems"
        :footer-props="footerProps"
        :loading="loading"
        must-sort
        class="elevation-1"
      >
        <!-- Status column -->
        <template #[`item.status`]="{ item }">
          <v-chip small dark :color="getStatusColor(item.status)">
            {{ getStatusLabel(item.status) }}
          </v-chip>
        </template>

        <!-- Actions -->
        <template #[`item.actions`]="{ item }">
          <v-btn icon small @click="viewItem(item)">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import trustx from '@/services/api/trustx.js'
import debounce from 'lodash/debounce'

const STATUS_KEYS = [
  'NOT_SPECIFIED',
  'COMPLETED_ENDED_SUCCESS',
  'COMPLETED_PROCESS_ERROR',
  'COMPLETED_TERMINATED',
  'COMPLETED_ENDED_ERROR',
  'PENDING',
  'ACTIVE',
  'REVIEW',
  'PAUSED'
]

const SORT_FIELD_MAP = {
  referenceId: 'id',
  applicantName: 'applicantName',
  dateCreated: 'createdAt',
  duration: 'elapsedMilliseconds',
  status: 'processInstanceStatus'
}

export default {
  name: 'ProcessInstances',

  data() {
    const now = new Date()
    const oneWeekAgo = new Date()
    oneWeekAgo.setDate(now.getDate() - 7)

    return {
      loading: false,
      fromMenu: false,
      toMenu: false,
      fromStep: 'date', // 'date' | 'time'
      toStep: 'date',

      filters: {
        status: 'NOT_SPECIFIED',
        fromDate: oneWeekAgo.toISOString().substr(0, 10), // YYYY-MM-DD
        fromTime: '00:00', // HH:mm
        toDate: now.toISOString().substr(0, 10),
        toTime: '23:59'
      },

      pagination: {
        groupBy: [],
        groupDesc: [],
        multiSort: false,
        mustSort: true,
        page: 1,
        itemsPerPage: 10,
        sortBy: ['dateCreated'],
        sortDesc: [true]
      },

      totalItems: 0,
      items: []
    }
  },

  computed: {
    statusItems() {
      return STATUS_KEYS.map(key => ({
        value: key,
        text: this.$t(`trustx.processInstances.statuses.${key}`)
      }))
    },

    headers() {
      const t = key => this.$t(`trustx.processInstances.table.${key}`)
      return [
        { text: t('referenceId'), value: 'referenceId' },
        { text: t('applicantName'), value: 'applicantName' },
        { text: t('dateCreated'), value: 'dateCreated' },
        { text: t('duration'), value: 'duration', sortable: false },
        { text: t('status'), value: 'status' },
        { text: t('reviewer'), value: 'reviewer', sortable: false },
        { text: t('actions'), value: 'actions', sortable: false }
      ]
    },

    footerProps() {
      return {
        'items-per-page-options': [10, 25, 50],
        'items-per-page-text': this.$t('trustx.processInstances.table.elementsPerPage')
      }
    }
  },

  watch: {
    pagination: {
      handler() {
        this.fetchData()
      },
      deep: true
    },
    filters: {
      handler() {
        // Reset to page 1 whenever a filter changes
        this.pagination.page = 1
        this.fetchData()
      },
      deep: true
    }
  },

  async mounted() {
    await this.fetchData()
  },

  methods: {
    formatDateTime(date, time) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      const d = new Date(year, month - 1, day)
      const monthName = d.toLocaleString('en-US', { month: 'short' })
      return `${monthName} ${day}, ${year} ${time || '00:00'}`
    },

    buildISODateTime(date, time) {
      if (!date) return undefined
      return new Date(`${date}T${time || '00:00'}:00`).toISOString()
    },
    fetchData: debounce(async function() {
      this.loading = true
      const sortField = SORT_FIELD_MAP[this.pagination.sortBy?.[0]] || 'createdAt'
      const sortDir = this.pagination.sortDesc?.[0] ? 'desc' : 'asc'

      try {
        const req = {
          page: this.pagination.page - 1,
          size: this.pagination.itemsPerPage,
          startDateTime: this.buildISODateTime(this.filters.fromDate, this.filters.fromTime),
          endDateTime: this.buildISODateTime(this.filters.toDate, this.filters.toTime),
          processInstanceStatus: this.filters.status !== 'NOT_SPECIFIED' ? this.filters.status : undefined,
          sort: `${sortField},${sortDir}`
        }
        // console.log({ req })
        const data = await trustx.listProcessInstances(req)

        this.totalItems = data.totalElements
        this.items = data.content.map(instance => ({
          referenceId: instance.processInstanceId,
          applicantName: instance.applicantName?.trim() || this.$t('trustx.notAvailable'),
          dateCreated: this.formatDate(instance.createdAt),
          duration: this.formatDuration(instance.elapsedMilliseconds),
          status: instance.processInstanceStatusMapped,
          reviewer: '-'
        }))
      } finally {
        this.loading = false
      }
    }, 50),

    clearFilters() {
      this.filters = {
        status: 'NOT_SPECIFIED',
        fromDate: null,
        fromTime: null,
        toDate: null,
        toTime: null
      }
    },

    getStatusColor(status) {
      const map = {
        Accepted: 'green',
        Abandoned: 'grey',
        Rejected: 'red'
      }
      return map[status] || 'blue'
    },

    getStatusLabel(status) {
      const key = `trustx.processInstances.status.${status}`
      return this.$te(key) ? this.$t(key) : status
    },

    viewItem(item) {
      this.$router.push({ path: `/trustx/process-instances/${item.referenceId}` })
    }
  }
}
</script>

<style scoped>
a { text-decoration: none; }
</style>
